import { Injectable, signal } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import { environment } from '../../environments/environment'
import {
  DismantlingArticle,
  CreateDismantlingArticleParams,
  UnclearDismantlingRecord
} from '../_models/dismantling-article.interface'

@Injectable({
  providedIn: 'root'
})
export class DismantlingArticleService {

  articles = signal<DismantlingArticle[]>([])
  unclearRecords = signal<UnclearDismantlingRecord[]>([])
  isLoading = signal<boolean>(false)

  constructor(
    private http: HttpClient,
  ) {
  }

  loadArticles(projectId: number) {
    const path = environment.apiURL + `projects/${projectId}/dismantling_articles.json`
    this.isLoading.set(true)
    this.http.get<DismantlingArticle[]>(path).subscribe({
      next: (articles) => {
        this.articles.set(articles)
        this.isLoading.set(false)
      },
      error: () => {
        this.isLoading.set(false)
      }
    })
  }

  createArticle(projectId: number, params: CreateDismantlingArticleParams) {
    const path = environment.apiURL + `projects/${projectId}/dismantling_articles.json`
    return this.http.post<DismantlingArticle>(path, { dismantling_article: params }).subscribe({
      next: (article) => {
        this.articles.update(list => [...list, article])
      }
    })
  }

  updateArticle(projectId: number, articleId: number, params: CreateDismantlingArticleParams) {
    const path = environment.apiURL + `projects/${projectId}/dismantling_articles/${articleId}.json`
    return this.http.patch<DismantlingArticle>(path, { dismantling_article: params }).subscribe({
      next: (updated) => {
        this.articles.update(list => list.map(a => a.id === articleId ? updated : a))
      }
    })
  }

  deleteArticle(projectId: number, articleId: number) {
    const path = environment.apiURL + `projects/${projectId}/dismantling_articles/${articleId}.json`
    return this.http.delete<{ success: boolean }>(path).subscribe({
      next: () => {
        this.articles.update(list => list.filter(a => a.id !== articleId))
      }
    })
  }

  loadUnclearRecords(projectId: number) {
    const path = environment.apiURL + `projects/${projectId}/dismantling_unclear_records.json`
    this.http.get<UnclearDismantlingRecord[]>(path).subscribe({
      next: (records) => {
        this.unclearRecords.set(records)
      }
    })
  }

  assignArticleToRecord(recordId: number, dismantlingArticleId: number) {
    const path = environment.apiURL + `dismantling_records/${recordId}/assign_article.json`
    return this.http.patch<UnclearDismantlingRecord>(path, { dismantling_article_id: dismantlingArticleId }).subscribe({
      next: (updated) => {
        this.unclearRecords.update(list => list.map(r => r.id === recordId ? updated : r))
      }
    })
  }

  getImageUrl(recordId: number, filename: string): string {
    return environment.apiURL + `dismantling_records/${recordId}/images/${filename}`
  }
}
